import logger from '~/services/logger';

export type ZappContextSetter = {
  enabled: boolean;
  extra_context_keys: string;
  remote_url?: string;
};

export type ClientContextKeys = Record<string, string | number | boolean>;

/**
 * Returns validated context keys from plugin config and optional client request values.
 *
 * Static `extra_context_keys` are merged first; client-provided keys override on conflict.
 *
 * @param zappContextSetter - Context Setter plugin configuration.
 * @param clientContextKeys - Context keys sent with a client feed request.
 * @returns Flat, validated context keys.
 */
export function getExtraContextKeys(
  zappContextSetter?: ZappContextSetter,
  clientContextKeys: ClientContextKeys = {}
): ClientContextKeys {
  const normalizedClientContextKeys: ClientContextKeys =
    normalizeContextKeys(clientContextKeys);

  let staticContextKeys: ClientContextKeys = {};

  try {
    if (zappContextSetter?.enabled) {
      staticContextKeys = parseStaticExtraContextKeys(
        zappContextSetter.extra_context_keys
      );
    }
  } catch (error: unknown) {
    logger.info(
      `getExtraContextKeys: failed to parse static context keys: ${
        error instanceof Error ? error.message : String(error)
      }`
    );
  }

  return {
    ...staticContextKeys,
    ...normalizedClientContextKeys,
  };
}

/**
 * Validates and normalizes flat context keys.
 *
 * @param contextKeys - Context keys to normalize.
 * @returns Valid primitive context keys only.
 */
export function normalizeContextKeys(
  contextKeys: ClientContextKeys
): ClientContextKeys {
  return Object.entries(contextKeys).reduce(
    (
      normalizedContextKeys: ClientContextKeys,
      [key, value]: [string, string | number | boolean]
    ) => {
      if (
        typeof value === 'string' ||
        typeof value === 'number' ||
        typeof value === 'boolean'
      ) {
        normalizedContextKeys[key] = value;
      }

      return normalizedContextKeys;
    },
    {}
  );
}

/**
 * Parses static Context Setter keys from plugin configuration JSON.
 *
 * @param extraContextKeysRaw - Raw `extra_context_keys` JSON string.
 * @returns Flat validated context keys.
 */
function parseStaticExtraContextKeys(
  extraContextKeysRaw: string
): ClientContextKeys {
  const parsedExtraContextKeys: Record<string, unknown> | 'parseError' =
    parseJsonSafely(extraContextKeysRaw);

  if (parsedExtraContextKeys === 'parseError') {
    throw new Error('Invalid JSON');
  }

  return normalizeContextKeys(flattenJSON(parsedExtraContextKeys));
}

function parseJsonSafely(str: string): Record<string, unknown> | 'parseError' {
  try {
    const parsedValue: unknown = JSON.parse(str);

    if (!parsedValue || typeof parsedValue !== 'object') {
      return 'parseError';
    }

    return parsedValue as Record<string, unknown>;
  } catch {
    return 'parseError';
  }
}

export function flattenJSON(obj: { [key: string]: any }): {
  [key: string]: any;
} {
  try {
    return Object.keys(obj).reduce((res: any, key: string) => {
      if (!isObject(obj[key])) {
        res[key] = obj[key];
        return res;
      }

      const flattenedSubObject = flattenJSON(obj[key]);

      res = Object.keys(flattenedSubObject).reduce((acc, subKey) => {
        acc[`${key}.${subKey}`] = flattenedSubObject[subKey];

        return acc;
      }, res);

      return res;
    }, {});
  } catch (error: any) {
    logger.info(`flattenJSON: ${error.message}`);
    return {};
  }
}

function isObject(value: any): boolean {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}
